import { useState, useMemo, useCallback, Fragment } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { Card, CardContent } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { PtsDailySpentHoursService, SchemaService } from "@/services/APIs";
import { get } from "@/services/axiosUitils";
import LoadingSpinner from "@/components/common/LoadingSpinner";
// import { Button } from "../../components/ui/button";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { Label } from "../../components/ui/label";
import "react-quill/dist/quill.snow.css";
import { buildSpentHoursStatusOptions } from "../EmployeeDashboard/spentHoursStatusHelpers";
import { spentHoursStatusPill } from "../EmployeeDashboard/spentHoursStatusPill";

const SPENT_HOURS_SCHEMA_NAME = "PTS Spent Hours";

function parseHoursToMinutes(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).trim();
  if (!text || text === "-" || text === "--") {
    return null;
  }

  if (text.includes(":")) {
    const [hPart, mPart] = text.split(":");
    const h = Number(hPart);
    const m = Number(mPart);
    if (Number.isFinite(h) && Number.isFinite(m)) {
      return h * 60 + m;
    }
  }

  const numeric = Number(text.replace(/h/gi, "").trim());
  if (Number.isFinite(numeric)) {
    return Math.round(numeric * 60);
  }

  return null;
}

const DailySpentHoursPage = () => {
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const [selectedDate, setSelectedDate] = useState<string>(() =>
    getTodayDate()
  );

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(SPENT_HOURS_SCHEMA_NAME),
  });

  const { data: spentHoursSchemaRecord } = useQuery({
    queryKey: ["schema", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(SPENT_HOURS_SCHEMA_NAME),
    enabled: !!SPENT_HOURS_SCHEMA_NAME,
  });

  const findSpentHoursFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [columnGridData]
  );

  const statusFieldName = findSpentHoursFieldName([
    "status",
    "approval_status",
  ]);

  const spentHoursStatusOptions = useMemo(
    () =>
      buildSpentHoursStatusOptions(
        spentHoursSchemaRecord,
        columnGridData?.grid,
        statusFieldName ?? "status"
      ),
    [spentHoursSchemaRecord, columnGridData, statusFieldName]
  );

  // Fetch daily spent hours data
  const { data: dailySpentHoursData, isLoading } = useQuery({
    queryKey: ["daily-spent-hours", selectedDate],
    queryFn: () =>
      PtsDailySpentHoursService.getDailySpentHours({
        date: selectedDate,
      }),
    enabled: !!selectedDate,
  });

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDisplayDate = useCallback((dateString: string): string => {
    if (!dateString) {
      return "";
    }
    try {
      const [year, month, day] = dateString.split("-");
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  }, []);

  // Format hours from "HH:MM" to "Xh Ym" format
  const formatHours = useCallback((hours: string | null): string => {
    if (!hours) {
      return "--";
    }
    try {
      const parts = hours.split(":");
      if (parts.length === 2) {
        const h = parseInt(parts[0], 10) || 0;
        const m = parseInt(parts[1], 10) || 0;
        if (h === 0 && m === 0) {
          return "--";
        }
        if (h === 0) {
          return `${m}m`;
        }
        if (m === 0) {
          return `${h}h`;
        }
        return `${h}h ${m}m`;
      }
      return hours;
    } catch {
      return hours || "--";
    }
  }, []);

  // Calculate total hours for work items
  const calculateTotalHours = useCallback(
    (workItems: Array<{ hours: string | null }>): string => {
      let totalMinutes = 0;

      workItems.forEach(item => {
        if (item.hours) {
          const parts = item.hours.split(":");
          if (parts.length === 2) {
            const hours = parseInt(parts[0], 10) || 0;
            const minutes = parseInt(parts[1], 10) || 0;
            totalMinutes += hours * 60 + minutes;
          }
        }
      });

      const totalHours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      if (totalHours === 0 && remainingMinutes === 0) {
        return "0h";
      }
      if (totalHours === 0) {
        return `${remainingMinutes}m`;
      }
      if (remainingMinutes === 0) {
        return `${totalHours}h`;
      }
      return `${totalHours}h ${remainingMinutes}m`;
    },
    []
  );

  // Group data by employee
  const groupedData = useMemo(() => {
    if (!dailySpentHoursData?.data) {
      return [];
    }

    const employeeMap = new Map<
      string,
      {
        employee: { id: string; name: string | null };
        workItems: Array<{
          project: string | null;
          task: string | null;
          task_id: string | null;
          sub_task: string | null;
          status: string | null;
          category: string | null;
          estimatedhours: string | number | null;
          worked_hours: string | number | null;
          hours: string | null;
          description: string | null;
          date: string | null;
          attendance_hours: string | null;
          difference_hours: string | null;
        }>;
        hasNoData: boolean;
        isAbsent: boolean;
      }
    >();

    dailySpentHoursData.data.forEach(item => {
      const employeeId = item.employee.id;
      const hasNoData =
        item.project === null &&
        item.task === null &&
        item.category === null &&
        item.hours === null &&
        item.description === null &&
        item.date === null;

      if (!employeeMap.has(employeeId)) {
        employeeMap.set(employeeId, {
          employee: item.employee,
          workItems: [],
          hasNoData: true,
          isAbsent: item.is_absent === true,
        });
      }

      const employeeData = employeeMap.get(employeeId)!;
      if (item.is_absent === true) {
        employeeData.isAbsent = true;
      }
      if (!hasNoData) {
        employeeData.hasNoData = false;
        employeeData.workItems.push({
          project: item.project,
          task: item.task,
          task_id: item.task_id ?? null,
          sub_task: item.sub_task ?? null,
          status: item.status ?? null,
          category: item.category,
          estimatedhours: item.estimatedhours ?? item.estimated_hours ?? null,
          worked_hours:
            item.worked_hours ?? item.workedHours ?? item.work_hours ?? null,
          hours: item.hours,
          description: item.description,
          date: item.date,
          attendance_hours: item.attendance_hours,
          difference_hours: item.difference_hours,
        });
      }
    });

    return Array.from(employeeMap.values());
  }, [dailySpentHoursData]);

  const uniqueTaskIdsForSubtasks = useMemo(() => {
    const ids = new Set<string>();
    for (const g of groupedData) {
      for (const w of g.workItems) {
        const tid = w.task_id;
        if (tid !== null && String(tid).trim() !== "") {
          ids.add(String(tid));
        }
      }
    }
    return Array.from(ids);
  }, [groupedData]);

  const subtaskQueries = useQueries({
    queries: uniqueTaskIdsForSubtasks.map(taskId => ({
      queryKey: ["task-subtask", "daily-spent-hours", taskId],
      queryFn: async () => {
        const response = await get<any>(
          "/pts_app/project-summary/task-subtask/",
          {
            params: { task_id: taskId },
          }
        );
        const listing = response?.subtask_listing;
        if (!Array.isArray(listing)) {
          return [] as { id: string; label: string }[];
        }
        return listing.map((st: any) => ({
          id: String(st.id),
          label: st.taskname ?? String(st.id),
        }));
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const subtaskIdToLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of subtaskQueries) {
      const rows = q.data;
      if (!Array.isArray(rows)) {
        continue;
      }
      for (const r of rows) {
        map.set(r.id, r.label);
      }
    }
    return map;
  }, [subtaskQueries]);

  const getSubTaskDisplay = useCallback(
    (raw: unknown) => {
      if (raw === null || raw === "") {
        return "--";
      }
      const s = String(raw).trim();
      if (!s) {
        return "--";
      }
      return subtaskIdToLabel.get(s) ?? s;
    },
    [subtaskIdToLabel]
  );

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Style for rendered ReactQuill HTML content in daily spent hours */
        .ql-editor-content {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .ql-editor-content p {
          margin: 0 0 8px 0;
          line-height: 1.6;
        }
        
        .ql-editor-content p:last-child {
          margin-bottom: 0;
        }
        
        .ql-editor-content ul,
        .ql-editor-content ol {
          margin: 8px 0;
          padding-left: 24px;
        }
        
        .ql-editor-content li {
          margin: 4px 0;
          line-height: 1.6;
        }
        
        .ql-editor-content strong {
          font-weight: 600;
        }
        
        .ql-editor-content em {
          font-style: italic;
        }
        
        .ql-editor-content u {
          text-decoration: underline;
        }
        
        .ql-editor-content s {
          text-decoration: line-through;
        }
        
        .ql-editor-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
        
        .ql-editor-content a:hover {
          color: #2563eb;
        }
        
        .ql-editor-content h1,
        .ql-editor-content h2,
        .ql-editor-content h3 {
          margin: 12px 0 8px 0;
          font-weight: 600;
        }
        
        .ql-editor-content h1 {
          font-size: 1.5em;
        }
        
        .ql-editor-content h2 {
          font-size: 1.3em;
        }
        
        .ql-editor-content h3 {
          font-size: 1.1em;
        }
      `}</style>
      <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
        <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-xl font-bold md:text-2xl">
              Daily Work Item Report
            </h1>
            {dailySpentHoursData?.date && (
              <p className="mt-1 text-sm text-gray-600">
                {formatDisplayDate(dailySpentHoursData.date)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* <Button
            //   onClick={handleExport}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
          >
            <Download className="h-4 w-4" />
            Export Report
          </Button> */}
            <div className="flex items-center gap-2">
              <Label htmlFor="date" className="text-sm font-medium">
                Date:
              </Label>
              <input
                id="date"
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-3 md:p-6">
            <div className="mb-4 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-0">
                <div className="grid w-full grid-cols-1 gap-3 sm:w-auto sm:grid-cols-3 sm:gap-5">
                  <div className="flex w-full flex-col items-center gap-2 rounded-md border border-gray-200 p-2">
                    <p className="text-sm font-medium">
                      Total No of Employees{" "}
                    </p>
                    <span className="text-xl font-bold text-blue-600">
                      {dailySpentHoursData?.total_employees ?? 0}
                    </span>
                  </div>
                  <div className="flex w-full flex-col items-center gap-2 rounded-md border border-gray-200 p-2">
                    <p className="text-sm font-medium">
                      Not Filled Daily Work Report{" "}
                    </p>
                    <span className="text-primary text-xl font-bold">
                      {dailySpentHoursData?.employees_without_data ?? 0}
                    </span>
                  </div>
                  <div className="flex w-full flex-col items-center gap-2 rounded-md border border-gray-200 p-2">
                    <p className="text-sm font-medium">Absent Employees </p>
                    <span className="text-xl font-bold text-red-500">
                      {dailySpentHoursData?.absent_count ?? 0}
                    </span>
                  </div>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="focus:ring-primary inline-flex items-center justify-center rounded-full text-red-500 transition-colors hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2"
                        aria-label="Information about employees"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm text-red-600">
                        Red Mark: Employee whose tasks are not filled.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {/* <p className="text-sm text-red-600">
              *** Red Mark: Employee whose tasks are not filled. ***
            </p> */}
            </div>

            <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead className="border-r border-s border-gray-200 px-4 py-3 font-semibold text-gray-700">
                      Project
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 font-semibold text-gray-700">
                      Task
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 font-semibold text-gray-700">
                      Sub task
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 text-center font-semibold text-gray-700">
                      Estimated hours
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 text-center font-semibold text-gray-700">
                      Worked hours
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 font-semibold text-gray-700">
                      Status
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 font-semibold text-gray-700">
                      Hours
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 font-semibold text-gray-700">
                      Description
                    </TableHead>
                    <TableHead className="border-r border-gray-200 px-4 py-3 font-semibold text-gray-700">
                      Fill From HelpDesk
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="py-8 text-center text-gray-500"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    groupedData.map((employeeGroup, index) => {
                      const employeeName =
                        employeeGroup.employee.name ?? "Unknown Employee";
                      const isNotFilled = employeeGroup.hasNoData;
                      const countNumber = index + 1;
                      const attendanceHours =
                        employeeGroup.workItems.find(
                          item => item.attendance_hours !== null
                        )?.attendance_hours ?? "-";
                      const differenceHours =
                        employeeGroup.workItems.find(
                          item => item.difference_hours !== null
                        )?.difference_hours ?? "-";
                      return (
                        <Fragment key={employeeGroup.employee.id}>
                          {/* Employee Header Row */}
                          {isNotFilled ? (
                            <TableRow className="border-b border-gray-200">
                              <TableCell
                                colSpan={9}
                                className="px-4 py-3 text-xs font-semibold text-red-600"
                              >
                                {countNumber}. {employeeName} -{" "}
                                {employeeGroup.isAbsent ? (
                                  <span className="text-xs font-bold text-red-600">
                                    Absent
                                  </span>
                                ) : (
                                  "Not Filled Task Hours"
                                )}
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow className="border-b-2 border-gray-300 bg-gray-50">
                              <TableCell
                                colSpan={9}
                                className="text-primary px-4 py-3 text-[13px] font-bold"
                              >
                                <div className="flex w-full items-center gap-2">
                                  <div>
                                    {countNumber}. {employeeName} - Total:{" "}
                                    {calculateTotalHours(
                                      employeeGroup.workItems
                                    )}{" "}
                                    Hours
                                  </div>
                                  <div className="font-medium text-gray-700">
                                    (Attendance Hours : {""}
                                    {attendanceHours})
                                  </div>
                                  <div
                                    className={`font-medium ${
                                      differenceHours === "-"
                                        ? "text-gray-700"
                                        : String(differenceHours).startsWith(
                                              "-"
                                            )
                                          ? "text-red-600"
                                          : "text-green-600"
                                    }`}
                                  >
                                    (Difference :{" "}
                                    {differenceHours === "-"
                                      ? "-"
                                      : String(differenceHours).replace(
                                          /^[+-]/,
                                          ""
                                        )}
                                    )
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Work Items */}
                          {!isNotFilled &&
                            employeeGroup.workItems.map(workItem => {
                              // Create a unique key without using array index
                              const uniqueKey = `${employeeGroup.employee.id}-${workItem.date ?? "no-date"}-${workItem.project ?? "no-project"}-${workItem.task ?? "no-task"}-${workItem.sub_task ?? "no-sub"}-${workItem.status ?? "no-status"}-${workItem.hours ?? "no-hours"}-${workItem.estimatedhours ?? "no-est"}-${workItem.worked_hours ?? "no-wh"}`;
                              const statusRaw = workItem.status;
                              const statusLabelResolved =
                                statusRaw !== null &&
                                String(statusRaw).trim() !== ""
                                  ? (spentHoursStatusOptions.find(
                                      o => String(o.value) === String(statusRaw)
                                    )?.label ?? String(statusRaw))
                                  : null;
                              const estimatedDisplay =
                                workItem.estimatedhours !== null &&
                                workItem.estimatedhours !== undefined &&
                                String(workItem.estimatedhours).trim() !== ""
                                  ? String(workItem.estimatedhours)
                                  : "--";
                              const workedDisplay =
                                workItem.worked_hours !== null &&
                                workItem.worked_hours !== undefined &&
                                String(workItem.worked_hours).trim() !== ""
                                  ? String(workItem.worked_hours)
                                  : "--";
                              const estimatedMinutes =
                                parseHoursToMinutes(estimatedDisplay);
                              const workedMinutes =
                                parseHoursToMinutes(workedDisplay);
                              const workedHoursColorClass =
                                estimatedMinutes !== null &&
                                workedMinutes !== null &&
                                workedMinutes > estimatedMinutes
                                  ? "text-red-600"
                                  : "text-gray-700";
                              return (
                                <TableRow
                                  key={uniqueKey}
                                  className="border-b border-gray-200 hover:bg-gray-50"
                                >
                                  <TableCell className="border-r border-gray-200 px-4 py-3 text-sm">
                                    {workItem.project ?? "--"}
                                  </TableCell>
                                  <TableCell className="border-r border-gray-200 px-4 py-3 text-sm">
                                    {workItem.task ?? "--"}
                                  </TableCell>
                                  <TableCell className="border-r border-gray-200 px-4 py-3 text-sm">
                                    {getSubTaskDisplay(workItem.sub_task)}
                                  </TableCell>
                                  <TableCell className="border-r border-gray-200 px-4 py-3 text-center text-sm text-gray-700">
                                    {estimatedDisplay}
                                  </TableCell>
                                  <TableCell
                                    className={`border-r border-gray-200 px-4 py-3 text-center text-sm ${workedHoursColorClass}`}
                                  >
                                    {workedDisplay}
                                  </TableCell>
                                  <TableCell className="border-r border-gray-200 px-4 py-3 text-sm">
                                    {statusLabelResolved ? (
                                      spentHoursStatusPill(
                                        statusLabelResolved,
                                        String(statusRaw)
                                      )
                                    ) : (
                                      <span className="pl-5">--</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="border-r border-gray-200 px-4 py-3 text-sm font-medium">
                                    {formatHours(workItem.hours)}
                                  </TableCell>
                                  <TableCell className="max-w-md break-words border-r border-gray-200 px-4 py-3 text-sm">
                                    {workItem.description &&
                                    workItem.description !== "--" ? (
                                      <div
                                        className="ql-editor-content prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            typeof workItem.description ===
                                            "string"
                                              ? workItem.description
                                              : String(
                                                  workItem.description ?? "--"
                                                ),
                                        }}
                                        style={{
                                          fontSize: "14px",
                                          lineHeight: "1.6",
                                          color: "#374151",
                                        }}
                                      />
                                    ) : (
                                      "--"
                                    )}
                                  </TableCell>
                                  <TableCell className="px-4 py-3 text-center text-sm">
                                    --
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default DailySpentHoursPage;
